import { memo, useId, useRef } from "react";
import type { RecenzjaMowca, RecenzjaWskazanie, RecenzjaWypowiedz } from "@shared/recenzjeTypy.js";
import { useAkcjePrzegladu } from "../../hooks/usePrzegladRecenzji.js";
import { ETYKIETY_JAKOSCI, ETYKIETY_RODZAJU, WARIANTY_JAKOSCI } from "../../lib/jakoscWypowiedzi.js";
import { jakoscDymka } from "../../lib/recenzja.js";
import { nazwaDodaniaUwagi, type WpisUwagi } from "../../lib/uwagiWypowiedzi.js";
import { IconDodajUwage } from "../icons/index.js";
import { poczatkiFragmentow, podzielNaFragmenty, type ZakresCytatu, type ZakresZaznaczenia } from "../transcript/fragmenty.js";
import { tekstZZaznaczeniem } from "../transcript/ZaznaczenieTrafien.js";
import { Avatar } from "../ui/Avatar.js";
import { Badge } from "../ui/Badge.js";
import { ListaUwag } from "./ListaUwag.js";
import { WskazanieRecenzji } from "./WskazanieRecenzji.js";

/**
 * Jeden dymek transkryptu w widoku recenzenta: numer wypowiedzi (od 1, jedyny numer w interfejsie), mowca, tekst
 * z podkresleniami cytatow, stopka z przyciskiem "Dodaj uwage", uwagi recenzenta i wskazania modelu pod spodem.
 *
 * Klasy transkryptu aplikacji (.msg, .bubble, .cytat-podswietlony, awatary), ale BEZ tego, czego recenzent nie ma
 * widziec: pomiaru mowy, ocen brzmienia i nazw modeli (ich w kontrakcie recenzenta w ogole nie ma). Czas jest tylko
 * napisem: nagrania nie ma, wiec nie ma czego przewijac.
 *
 * Stopka jest przy KAZDEJ wypowiedzi, bo przycisk "Dodaj uwage" jest przy kazdej. Formularz i zapisane uwagi stoja
 * zaraz pod stopka, nad wskazaniami modelu. Komponent jest zapamietany (`memo`): dostaje tylko wpisy swojej wypowiedzi
 * o stalej tozsamosci, wiec zapis uwagi przy jednej wypowiedzi nie przerysowuje setek pozostalych.
 */
/** Wspolna pusta lista: wiersz bez zaznaczen dostaje zawsze te sama referencje, wiec `memo` go nie przerysowuje. */
const BRAK_ZAZNACZEN: readonly ZakresZaznaczenia[] = [];

export const WypowiedzRecenzji = memo(function WypowiedzRecenzji({
  wypowiedz,
  mowca,
  zakresy,
  wskazania,
  wpisy,
  wyrozniona = false,
  zaznaczenia = BRAK_ZAZNACZEN,
}: {
  wypowiedz: RecenzjaWypowiedz;
  mowca: RecenzjaMowca | undefined;
  zakresy: readonly ZakresCytatu[];
  wskazania: readonly RecenzjaWskazanie[];
  wpisy: readonly WpisUwagi[];
  /** Wypowiedz pokazana przed chwila z karty "Powtarzane slowa": obrys dymku na chwile (.msg.is-wyrozniona). */
  wyrozniona?: boolean;
  /** Trafienia slowa z karty "Powtarzane slowa", zaznaczone na czas wyroznienia (juz sprawdzone: zaznaczeniaWTekscie). */
  zaznaczenia?: readonly ZakresZaznaczenia[];
}): React.JSX.Element {
  const akcje = useAkcjePrzegladu();
  const idUwag = useId();
  const refWiersza = useRef<HTMLLIElement>(null);
  const refDodaj = useRef<HTMLButtonElement>(null);
  const nazwa = mowca?.nazwa ?? "Mówca";
  const jakosc = jakoscDymka(wypowiedz.kolor);
  const fragmenty = podzielNaFragmenty(wypowiedz.tekst, zakresy);
  const poczatki = poczatkiFragmentow(fragmenty);
  const maUwage = wpisy.some((w) => w.uwaga !== null);
  const otwarte = wpisy.some((w) => w.otwarta);

  function dodajUwage(): void {
    // Pusty formularz nowej uwagi jest juz otwarty: nie mnozymy ich, tylko wracamy do niego.
    const pusty = wpisy.find((w) => w.otwarta && w.uwaga === null);
    if (pusty) {
      const formularz = Array.from(refWiersza.current?.querySelectorAll<HTMLElement>("[data-klucz]") ?? []).find(
        (el) => el.getAttribute("data-klucz") === pusty.klucz,
      );
      formularz?.querySelector("textarea")?.focus();
      return;
    }
    akcje.otworzUwage(wypowiedz.numer);
  }

  return (
    <li
      className={`msg${wyrozniona ? " is-wyrozniona" : ""}`}
      data-tint={mowca?.tint ?? 1}
      data-numer={wypowiedz.numer}
      data-jakosc={jakosc}
      ref={refWiersza}
      // Cel fokusu po przejsciu z karty "Powtarzane slowa" (czytnik i klawiatura laduja tam, gdzie oko), poza kolejnoscia Taba.
      tabIndex={-1}
    >
      <div className="msg__ident">
        <span className="msg__numer">
          <span className="tylko-dla-czytnika">Wypowiedź </span>
          {wypowiedz.numer}
        </span>
        <span className="msg__avatar">
          <Avatar nazwa={nazwa} rozmiar="s" />
        </span>
        <span className="msg__nazwa">{nazwa}</span>
        {/* A-7: kolor dymka nie niesie znaczenia sam, wiec jakosc ma tez slowo, a zlota i szara plakietka kropke (jak
            w transkrypcie aplikacji, Wypowiedz.tsx): neutralna "srednie" zostaje bez oznaczenia. */}
        {jakosc && (
          <Badge wariant={WARIANTY_JAKOSCI[jakosc]} kropka>
            {ETYKIETY_JAKOSCI[jakosc]}
          </Badge>
        )}
        {maUwage && <Badge>Twoja uwaga</Badge>}
      </div>

      <div className="bubble">
        {fragmenty.map((f, i) => {
          if (f.rodzaj !== "cytat") return <span key={i}>{tekstZZaznaczeniem(f.tekst, poczatki[i], zaznaczenia)}</span>;
          return (
            <span className="cytat-podswietlony" data-rodzaj={f.rodzajWskazania} key={i}>
              {tekstZZaznaczeniem(f.tekst, poczatki[i], zaznaczenia)}
              <span className="tylko-dla-czytnika">{` (cytat ze wskazania: ${ETYKIETY_RODZAJU[f.rodzajWskazania]})`}</span>
            </span>
          );
        })}
      </div>

      <div className="msg__stopka">
        {wypowiedz.czas && <span className="msg__czas msg__czas--statyczny">{wypowiedz.czas}</span>}
        <button
          ref={refDodaj}
          type="button"
          className="rec-btn-tekst"
          aria-label={nazwaDodaniaUwagi(wypowiedz.numer)}
          aria-expanded={otwarte}
          aria-controls={otwarte ? idUwag : undefined}
          onClick={dodajUwage}
        >
          <IconDodajUwage size={16} />
          Dodaj uwagę
        </button>
      </div>

      <ListaUwag wpisy={wpisy} id={idUwag} przyciskDodaj={refDodaj} />

      {wskazania.map((w) => (
        <WskazanieRecenzji key={w.klucz} wskazanie={w} />
      ))}
    </li>
  );
});
